/**
 * Color utilities for canvas selection and connector colors
 */

/**
 * Parse a hex color string to RGB components
 * Supports 3-digit (#RGB) and 6-digit (#RRGGBB) formats
 */
export function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  // Remove # if present
  const cleanHex = hex.replace(/^#/, '');

  // Handle 3-digit hex (e.g., #F00 -> #FF0000)
  let normalizedHex = cleanHex;
  if (cleanHex.length === 3) {
    normalizedHex = cleanHex
      .split('')
      .map((c) => c + c)
      .join('');
  }

  // Validate hex format
  if (!/^[0-9A-Fa-f]{6}$/.test(normalizedHex)) {
    return null;
  }

  const r = parseInt(normalizedHex.substring(0, 2), 16);
  const g = parseInt(normalizedHex.substring(2, 4), 16);
  const b = parseInt(normalizedHex.substring(4, 6), 16);

  return { r, g, b };
}

/**
 * Convert RGB components to hex color string
 */
export function rgbToHex(r: number, g: number, b: number): string {
  const clamp = (v: number) => Math.max(0, Math.min(255, Math.round(v)));
  const toHex = (v: number) => clamp(v).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Compute the average of multiple colors
 * Returns the averaged color as a hex string
 * 
 * @param colors - Array of hex color strings (e.g., ['#FF0000', '#00FF00'])
 * @returns The averaged color as hex string, or null if no valid colors provided
 */
export function averageColors(colors: string[]): string | null {
  if (!colors || colors.length === 0) {
    return null;
  }

  // Parse all valid colors
  const validRgbColors = colors
    .map((c) => hexToRgb(c))
    .filter((rgb): rgb is { r: number; g: number; b: number } => rgb !== null);

  if (validRgbColors.length === 0) {
    return null;
  }

  // If only one valid color, return it directly
  if (validRgbColors.length === 1) {
    const firstColor = validRgbColors[0]!;
    return rgbToHex(firstColor.r, firstColor.g, firstColor.b);
  }

  // Compute the average of each channel
  const avgR = validRgbColors.reduce((sum, c) => sum + c.r, 0) / validRgbColors.length;
  const avgG = validRgbColors.reduce((sum, c) => sum + c.g, 0) / validRgbColors.length;
  const avgB = validRgbColors.reduce((sum, c) => sum + c.b, 0) / validRgbColors.length;

  return rgbToHex(avgR, avgG, avgB);
}

/**
 * Default selection color when no connector color is available
 * This is the current green color used for edit mode
 */
export const DEFAULT_SELECTION_COLOR = '#107c10'; // Fluent UI green (colorPaletteGreenBackground3 equivalent)
